import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Briefcase, DollarSign, CheckCircle, Clock, Activity, FileText, TrendingUp, TrendingDown, AlertCircle, Home, User, Bell, Calendar, BarChart3, Settings, Mail } from 'lucide-react';
import { useAuthStore } from '@/store/authStore';
import { Card } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { Badge } from '@/components/ui/Badge';
import { Breadcrumb } from '@/components/ui/Breadcrumb';
import { SkipLink } from '@/components/ui/SkipLink';
import { ProfileStatusBanner } from '@/components/provider/ProfileStatusBanner';
import { CompleteJobModal } from '@/components/job/CompleteJobModal';
import { SkeletonCard, SkeletonJobCard } from '@/components/ui/SkeletonLoaders';
import { EnhancedEmptyState } from '@/components/ui/EnhancedEmptyState';
import { CircularProgress } from '@/components/ui/ProgressIndicator';
import { FadeIn } from '@/components/ui/AnimatedComponents';
import { invoiceService } from '@/services/invoiceService';
import { companyService } from '@/services/companyService';
import { notificationService } from '@/services/notificationService';
import { useGlobalToast } from '@/contexts/ToastContext';
import { ENV } from '@/config/env';
import { quoteTemplateService } from '@/services/quoteTemplateService';

export function ProviderDashboardPage() {
  const navigate = useNavigate();
  const { user } = useAuthStore();
  const { success, error } = useGlobalToast();
  
  const [quotesSent, setQuotesSent] = useState<any[]>([]);
  const [activeJobs, setActiveJobs] = useState<any[]>([]);
  const [completedJobs, setCompletedJobs] = useState<any[]>([]);
  const [availableJobs, setAvailableJobs] = useState<any[]>([]);
  const [pendingInvitations, setPendingInvitations] = useState<any[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [showCompleteModal, setShowCompleteModal] = useState(false);
  const [selectedJob, setSelectedJob] = useState<any>(null);
  const [previousJobCount, setPreviousJobCount] = useState(0);
  const [currentCompanyName, setCurrentCompanyName] = useState<string | null>(null);
  const [templateStats, setTemplateStats] = useState({
    totalTemplates: 0,
    totalUsage: 0,
    averageSuccessRate: 0
  });
  const [dismissedTips, setDismissedTips] = useState<string[]>([]);
  const [showFabMenu, setShowFabMenu] = useState(false);

  useEffect(() => {
    fetchAllData();
    
    // Real-time polling for new jobs
    const interval = setInterval(() => {
      checkForNewJobs();
    }, 30000); // Every 30 seconds
    
    return () => clearInterval(interval);
  }, []);

  const fetchAllData = async () => {
    setIsLoading(true);
    await Promise.all([
      fetchQuotesSent(),
      fetchActiveJobs(),
      fetchCompletedJobs(),
      fetchAvailableJobs(),
      fetchTemplateStats(),
      fetchCurrentCompany(),
      fetchPendingInvitations()
    ]);
    setIsLoading(false);
  };

  const fetchPendingInvitations = async () => {
    if (!user?.email) return;
    
    try {
      const invitations = await companyService.getReceivedInvitations(user.email);
      const pending = invitations.filter(inv => inv.status === 'pending');
      setPendingInvitations(pending);

      if (pending.length > 0 && user?.id) {
        const seenKey = 'seen_company_invite_ids';
        let seenIds = new Set<string>();

        try {
          const stored = localStorage.getItem(seenKey);
          if (stored) {
            seenIds = new Set<string>(JSON.parse(stored));
          }
        } catch (storageError) {
          // Failed to read from storage
        }

        const newInvites = pending.filter(inv => !seenIds.has(inv.id));
        if (newInvites.length > 0) {
          newInvites.forEach(invitation => {
            notificationService.createNotification(
              user.id,
              'company_invitation',
              'Company invitation received',
              `${invitation.companyName} invited you to join their team.`,
              { invitationId: invitation.id, companyId: invitation.companyId }
            );
          });

          const alertMessage = newInvites.length === 1
            ? `You have a new invitation from ${newInvites[0].companyName}.`
            : `You have ${newInvites.length} new company invitations.`;
          success('Invitation Alert', alertMessage);

          try {
            newInvites.forEach(invite => seenIds.add(invite.id));
            localStorage.setItem(seenKey, JSON.stringify(Array.from(seenIds)));
          } catch (storageError) {
            // Failed to store to storage
          }
        }
      }
    } catch (err) {
      // Failed to fetch invitations
    }
  };

  const fetchCurrentCompany = async () => {
    if (!user?.id) return;

    try {
      const response = await fetch(
        `${ENV.API_BASE_URL}/api/v1/auth/providers/profile`,
        {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('auth_token')}`,
            'X-User-ID': user.id,
            'Content-Type': 'application/json'
          }
        }
      );

      if (!response.ok) {
        return;
      }

      const data = await response.json();
      const companyName = data.current_company_name || '';
      setCurrentCompanyName(companyName || null);
    } catch (err) {
      // Failed to fetch current company
    }
  };

  const fetchQuotesSent = async () => {
    if (!user?.id) return;
    
    try {
      const response = await fetch(
        `${ENV.API_BASE_URL}/api/v1/auth/providers/quotes/sent?status=pending`,
        { 
          headers: { 
            'Authorization': `Bearer ${localStorage.getItem('auth_token')}`,
            'X-User-ID': user.id,
            'Content-Type': 'application/json'
          } 
        }
      );
      if (response.ok) {
        const data = await response.json();
        setQuotesSent(data.data || []);
      }
    } catch (err) {
      // Failed to fetch quotes
    }
  };

  const fetchActiveJobs = async () => {
    if (!user?.id) return;
    
    try {
      // Fetch both quote-accepted and in-progress jobs
      const [quoteAcceptedResponse, inProgressResponse] = await Promise.all([
        fetch(
          `${ENV.API_BASE_URL}/api/v1/auth/providers/jobs/active?status=quote-accepted`,
          { 
            headers: { 
              'Authorization': `Bearer ${localStorage.getItem('auth_token')}`,
              'X-User-ID': user.id,
              'Content-Type': 'application/json'
            } 
          }
        ),
        fetch(
          `${ENV.API_BASE_URL}/api/v1/auth/providers/jobs/active`,
          { 
            headers: { 
              'Authorization': `Bearer ${localStorage.getItem('auth_token')}`,
              'X-User-ID': user.id,
              'Content-Type': 'application/json'
            } 
          }
        )
      ]);
      
      const allJobs: any[] = [];
      
      if (quoteAcceptedResponse.ok) {
        const quoteAcceptedData = await quoteAcceptedResponse.json();
        const quoteAcceptedJobs = quoteAcceptedData.data || [];
        // Filter for quote-accepted status
        allJobs.push(...quoteAcceptedJobs.filter((job: any) => job.status === 'quote-accepted'));
      }
      
      if (inProgressResponse.ok) {
        const inProgressData = await inProgressResponse.json();
        const inProgressJobs = inProgressData.data || [];
        // Filter for in-progress status
        allJobs.push(...inProgressJobs.filter((job: any) => job.status === 'in-progress'));
      }
      
      setActiveJobs(allJobs);
    } catch (err) {
      // Failed to fetch active jobs
    }
  };

  const handleStartJob = async (jobId: string) => {
    try {
      const response = await fetch(
        `${ENV.API_BASE_URL}/api/v1/auth/providers/jobs/${jobId}/start`,
        {
          method: 'PUT',
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('auth_token')}`,
            'Content-Type': 'application/json'
          }
        }
      );

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || 'Failed to start job');
      }

      success('Job Started', 'You have started the job. Work can begin now!');
      await fetchActiveJobs();
      await fetchCompletedJobs();
    } catch (err: any) {
      error('Unable to Start Job', err.message || 'Could not start the job. Please try again');
    }
  };

  const handleCompleteJobSimple = async (jobId: string) => {
    try {
      const response = await fetch(
        `${ENV.API_BASE_URL}/api/v1/auth/providers/jobs/${jobId}/complete`,
        {
          method: 'PUT',
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('auth_token')}`,
            'Content-Type': 'application/json'
          }
        }
      );

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || 'Failed to complete job');
      }

      success('Job Completed', 'Job has been marked as completed successfully!');
      await fetchActiveJobs();
      await fetchCompletedJobs();
    } catch (err: any) {
      error('Unable to Complete Job', err.message || 'Could not mark job as complete. Please try again');
    }
  };

  const fetchCompletedJobs = async () => {
    if (!user?.id) return;
    
    try {
      const response = await fetch(
        `${ENV.API_BASE_URL}/api/v1/auth/providers/jobs/completed`,
        { 
          headers: { 
            'Authorization': `Bearer ${localStorage.getItem('auth_token')}`,
            'X-User-ID': user.id,
            'Content-Type': 'application/json'
          } 
        }
      );
      if (response.ok) {
        const data = await response.json();
        setCompletedJobs(data.data || []);
      }
    } catch (err) {
      // Failed to fetch completed jobs
    }
  };

  const fetchAvailableJobs = async () => {
    if (!user?.id) {
      return;
    }
    
    try {
      const response = await fetch(
        `${ENV.API_BASE_URL}/api/v1/auth/providers/jobs/available`,
        { 
          headers: { 
            'Authorization': `Bearer ${localStorage.getItem('auth_token')}`,
            'X-User-ID': user.id,
            'Content-Type': 'application/json'
          } 
        }
      );
      
      if (!response.ok) {
        return;
      }
      
      const data = await response.json();
      setAvailableJobs(data.data || []);
    } catch (err) {
      // Failed to fetch available jobs
    }
  };

  const fetchTemplateStats = async () => {
    if (!user) return;
    try {
      const stats = await quoteTemplateService.getTemplateStats(user.id);
      setTemplateStats({
        totalTemplates: stats.totalTemplates,
        totalUsage: stats.totalUsage,
        averageSuccessRate: stats.averageSuccessRate
      });
    } catch (err) {
      // Failed to fetch template stats
    }
  };

  const checkForNewJobs = async () => {
    try {
      const response = await fetch(
        `${ENV.API_BASE_URL}/api/v1/auth/providers/jobs/available`,
        { headers: { 'Authorization': `Bearer ${localStorage.getItem('auth_token')}` } }
      );
      if (response.ok) {
        const data = await response.json();
        const newCount = data.data?.length || 0;
        
        if (previousJobCount > 0 && newCount > previousJobCount) {
          success('New Jobs Available!', `${newCount - previousJobCount} new jobs match your profile`);
        }
        
        setPreviousJobCount(newCount);
        setAvailableJobs(data.data || []);
      }
    } catch (err) {
      // Failed to check for new jobs
    }
  };

  const handleCompleteJob = async (data: any) => {
    if (!selectedJob) return;
    
    try {
      await invoiceService.createInvoice({
        jobId: selectedJob.id,
        finalAmount: data.finalAmount,
        workDescription: data.workDescription,
        paymentDetails: data.paymentDetails
      });
      
      fetchActiveJobs();
      fetchCompletedJobs();
      
      success('Job Completed', 'Invoice has been sent to the customer');
    } catch (err: any) {
      throw new Error(err.message || 'Unable to complete job. Please try again');
    }
  };

  const getRelativeTime = (dateString: string) => {
    const now = new Date();
    const date = new Date(dateString);
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    return date.toLocaleDateString();
  };

  const dismissTip = (tipId: string) => {
    setDismissedTips([...dismissedTips, tipId]);
    localStorage.setItem('dismissedTips', JSON.stringify([...dismissedTips, tipId]));
  };

  // Load dismissed tips from localStorage
  useEffect(() => {
    const saved = localStorage.getItem('dismissedTips');
    if (saved) {
      setDismissedTips(JSON.parse(saved));
    }
  }, []);

  // Close FAB menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as HTMLElement;
      if (showFabMenu && !target.closest('.fab-container')) {
        setShowFabMenu(false);
      }
    };

    if (showFabMenu) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showFabMenu]);

  // Calculate stats
  const thisMonthEarnings = completedJobs
    .filter(j => new Date(j.completed_date).getMonth() === new Date().getMonth())
    .reduce((sum, j) => sum + (j.amount || 0), 0);
  
  const lastMonthEarnings = completedJobs
    .filter(j => {
      const date = new Date(j.completed_date);
      const lastMonth = new Date();
      lastMonth.setMonth(lastMonth.getMonth() - 1);
      return date.getMonth() === lastMonth.getMonth();
    })
    .reduce((sum, j) => sum + (j.amount || 0), 0);
  
  const earningsTrend = lastMonthEarnings > 0 
    ? Math.round(((thisMonthEarnings - lastMonthEarnings) / lastMonthEarnings) * 100)
    : 0;

  const completionRate = activeJobs.length + completedJobs.length > 0
    ? Math.round((completedJobs.length / (activeJobs.length + completedJobs.length)) * 100)
    : 0;

  const quoteWinRate = quotesSent.length > 0
    ? Math.round((activeJobs.length / quotesSent.length) * 100)
    : 0;

  // Action required items
  const urgentQuotes = quotesSent.filter(q => {
    const created = new Date(q.created_at);
    const hoursSince = (new Date().getTime() - created.getTime()) / (1000 * 60 * 60);
    return hoursSince > 24;
  }).length;

  const jobsDueToday = activeJobs.filter(j => {
    // Check if job has a due date and it's today
    return false; // Placeholder - implement when due dates are added
  }).length;

  const newJobsToday = availableJobs.filter(j => {
    const created = new Date(j.created_at);
    const today = new Date();
    return created.toDateString() === today.toDateString();
  }).length;

  return (
    <div className="min-h-screen bg-gray-50 text-gray-900 pb-20 md:pb-8">
      <SkipLink href="#main-content">Skip to main content</SkipLink>
      
      <main id="main-content" role="main" className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 sm:py-8">
        <Breadcrumb items={[{ label: 'Home', href: '/' }, { label: 'Dashboard' }]} />
        
        {/* Compact Profile Status Banner */}
        <ProfileStatusBanner />

        {currentCompanyName && (
          <Card className="mb-6 border-l-4 border-l-blue-500 mt-4">
            <div className="p-4 flex items-center justify-between">
              <div>
                <p className="text-xs uppercase tracking-wide text-gray-500">Company</p>
                <p className="text-lg font-semibold text-gray-900">{currentCompanyName}</p>
              </div>
              <Badge variant="secondary" size="sm">Team Member</Badge>
            </div>
          </Card>
        )}

        {/* Welcome Header */}
        <div className="mb-4 sm:mb-6 mt-4">
          <h1 className="text-2xl sm:text-3xl font-bold text-gray-900">
            Hello, {user?.businessName || user?.firstName}
          </h1>
          <p className="text-sm sm:text-base text-gray-600 mt-1">Here's your performance overview</p>
        </div>

        {/* Action Required Section */}
        {(urgentQuotes > 0 || jobsDueToday > 0 || newJobsToday > 0 || pendingInvitations.length > 0) && (
          <Card className="mb-4 sm:mb-6 border-l-4 border-l-red-500">
            <div className="p-4">
              <h2 className="font-bold text-gray-900 mb-3 flex items-center gap-2">
                <AlertCircle className="w-5 h-5 text-red-600" aria-hidden="true" />
                ⚡ Action Required
              </h2>
              <div className="space-y-2">
                {pendingInvitations.length > 0 && (
                  <div className="flex items-center justify-between p-3 bg-blue-50 rounded-lg border border-blue-200">
                    <div className="flex items-center gap-2">
                      <Mail className="w-5 h-5 text-blue-600" aria-hidden="true" />
                      <span className="text-sm text-blue-900 font-medium">
                        🔵 {pendingInvitations.length} company invitation{pendingInvitations.length > 1 ? 's' : ''} pending
                      </span>
                    </div>
                    <Button size="sm" onClick={() => navigate('/invitations')} aria-label={`View ${pendingInvitations.length} pending invitation${pendingInvitations.length > 1 ? 's' : ''}`}>
                      View
                    </Button>
                  </div>
                )}
                {urgentQuotes > 0 && (
                  <div className="flex items-center justify-between p-3 bg-red-50 rounded-lg border border-red-200">
                    <span className="text-sm text-red-900 font-medium">
                      🔴 {urgentQuotes} quotes pending over 24 hours
                    </span>
                    <Button size="sm" onClick={() => navigate('/provider/quotes')} aria-label={`Review ${urgentQuotes} urgent quotes`}>
                      Review
                    </Button>
                  </div>
                )}
                {jobsDueToday > 0 && (
                  <div className="flex items-center justify-between p-3 bg-yellow-50 rounded-lg border border-yellow-200">
                    <span className="text-sm text-yellow-900 font-medium">
                      🟡 {jobsDueToday} jobs due today
                    </span>
                    <Button size="sm" onClick={() => navigate('/provider/my-jobs?tab=active')} aria-label={`View ${jobsDueToday} jobs due today`}>
                      View
                    </Button>
                  </div>
                )}
                {newJobsToday > 0 && (
                  <div className="flex items-center justify-between p-3 bg-green-50 rounded-lg border border-green-200">
                    <span className="text-sm text-green-900 font-medium">
                      🟢 {newJobsToday} new jobs posted today
                    </span>
                    <Button size="sm" onClick={() => navigate('/provider-jobs')} aria-label={`Browse ${newJobsToday} new jobs`}>
                      Browse
                    </Button>
                  </div>
                )}
              </div>
            </div>
          </Card>
        )}

        <div className="grid lg:grid-cols-3 gap-6">
          <div className="lg:col-span-2 space-y-6">
            {/* Quotes Card */}
            <Card>
              <div className="p-4 border-b border-gray-200 flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <FileText className="w-5 h-5 text-warning-600" />
                  <h2 className="text-lg font-bold text-gray-900">Quotes</h2>
                  <Badge variant="warning" size="sm">{quotesSent.length} Pending</Badge>
                </div>
                <Button variant="outline" size="sm" onClick={() => navigate('/provider/quotes')}>
                  View All
                </Button>
              </div>
              
              <div className="p-4">
                {isLoading ? (
                  <div className="space-y-3">
                    <SkeletonCard />
                    <SkeletonCard />
                    <SkeletonCard />
                  </div>
                ) : quotesSent.length === 0 ? (
                  <div className="text-center py-8">
                    <Clock className="w-12 h-12 mx-auto mb-3 text-gray-300" />
                    <p className="text-sm text-gray-600 mb-2">No pending quotes</p>
                    <p className="text-xs text-gray-500 mb-4">
                      💡 Tip: Respond within 2 hours to increase win rate by 40%
                    </p>
                    <Button size="sm" onClick={() => navigate('/provider-jobs')}>
                      Browse Jobs
                    </Button>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {quotesSent.slice(0, 3).map(quote => {
                      const hoursSince = (new Date().getTime() - new Date(quote.created_at).getTime()) / (1000 * 60 * 60);
                      const isUrgent = hoursSince > 24;
                      
                      return (
                        <div 
                          key={quote.id}
                          onClick={() => navigate(`/jobs/${quote.job_id || quote.id}`)}
                          className="p-4 bg-white border-l-4 border-l-warning-500 rounded-lg shadow-sm hover:shadow-md transition-shadow cursor-pointer"
                        >
                          <div className="flex justify-between items-start mb-2">
                            <h4 className="font-medium text-gray-900">{quote.title}</h4>
                            {isUrgent && (
                              <Badge variant="error" size="sm">Urgent</Badge>
                            )}
                          </div>
                          <p className="text-sm text-gray-600 line-clamp-1 mb-2">{quote.description}</p>
                          <div className="flex items-center justify-between text-xs text-gray-500">
                            <span>€{quote.amount?.toLocaleString()}</span>
                            <span>Sent {getRelativeTime(quote.created_at)}</span>
                          </div>
                        </div>
                      );
                    })}
                    {quotesSent.length > 3 && (
                      <Button variant="ghost" size="sm" fullWidth onClick={() => navigate('/provider/quotes')}>
                        View All {quotesSent.length} Quotes
                      </Button>
                    )}
                  </div>
                )}
              </div>
            </Card>

            {/* Active Jobs Card */}
            <Card>
              <div className="p-4 border-b border-gray-200 flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Activity className="w-5 h-5 text-primary-600" />
                  <h2 className="text-lg font-bold text-gray-900">Active Jobs</h2>
                  <Badge variant="success" size="sm">{activeJobs.length}</Badge>
                </div>
                <Button variant="outline" size="sm" onClick={() => navigate('/provider/my-jobs?tab=active')}>
                  View All
                </Button>
              </div>
              
              <div className="p-4">
                {isLoading ? (
                  <div className="space-y-3">
                    <SkeletonCard />
                    <SkeletonCard />
                    <SkeletonCard />
                  </div>
                ) : activeJobs.length === 0 ? (
                  <div className="text-center py-8">
                    <Activity className="w-12 h-12 mx-auto mb-3 text-gray-300" />
                    <p className="text-sm text-gray-600 mb-2">No active jobs</p>
                    <p className="text-xs text-gray-500 mb-4">Accepted jobs will appear here</p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {activeJobs.slice(0, 3).map(job => {
                      const isQuoteAccepted = job.status === 'quote-accepted';
                      const isInProgress = job.status === 'in-progress';
                      const scheduledDate = job.scheduled_date ? new Date(job.scheduled_date) : null;
                      const today = new Date();
                      today.setHours(0, 0, 0, 0);
                      const isScheduledToday = scheduledDate && scheduledDate.toDateString() === today.toDateString();

                      return (
                        <div 
                          key={job.id}
                          onClick={() => navigate(`/jobs/${job.id}`)}
                          className={`p-4 bg-white border-l-4 rounded-lg shadow-sm hover:shadow-md transition-shadow cursor-pointer ${
                            isQuoteAccepted ? 'border-l-blue-500' : 'border-l-primary-500'
                          }`}
                        >
                          <div className="flex justify-between items-start mb-2">
                            <h4 className="font-medium text-gray-900">{job.title}</h4>
                            {isQuoteAccepted ? (
                              <Badge variant="secondary" size="sm">Quote Accepted</Badge>
                            ) : (
                              <Badge variant="success" size="sm">In Progress</Badge>
                            )}
                          </div>
                          <p className="text-sm text-gray-600 line-clamp-1 mb-2">{job.description}</p>
                          {isQuoteAccepted && scheduledDate && (
                            <p className="text-xs text-gray-500 mb-3 flex items-center gap-1">
                              <Calendar className="w-3 h-3" />
                              Scheduled: {scheduledDate.toLocaleDateString('en-US', { 
                                weekday: 'short', 
                                month: 'short', 
                                day: 'numeric',
                                year: 'numeric'
                              })}
                            </p>
                          )}
                          <div className="flex items-center gap-2" onClick={(e) => e.stopPropagation()}>
                            <Button 
                              size="sm" 
                              variant="primary" 
                              onClick={() => navigate(`/jobs/${job.id}`)}
                            >
                              View Details
                            </Button>
                            {isQuoteAccepted && isScheduledToday && (
                              <Button 
                                size="sm" 
                                variant="primary"
                                onClick={() => handleStartJob(job.id)}
                              >
                                Start Job
                              </Button>
                            )}
                            {isInProgress && (
                              <Button 
                                size="sm" 
                                variant="outline"
                                onClick={() => handleCompleteJobSimple(job.id)}
                              >
                                Mark Complete
                              </Button>
                            )}
                          </div>
                        </div>
                      );
                    })}
                    {activeJobs.length > 3 && (
                      <Button variant="ghost" size="sm" fullWidth onClick={() => navigate('/provider/my-jobs?tab=active')}>
                        View All {activeJobs.length} Active Jobs
                      </Button>
                    )}
                  </div>
                )}
              </div>
            </Card>

            {/* Available Jobs Card */}
            <Card>
              <div className="p-4 border-b border-gray-200 flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Briefcase className="w-5 h-5 text-success-600" />
                  <h2 className="text-lg font-bold text-gray-900">New Opportunities</h2>
                  <Badge variant="success" size="sm">{availableJobs.length} New</Badge>
                </div>
                <Button variant="primary" size="sm" onClick={() => navigate('/provider-jobs')}>
                  Browse All
                </Button>
              </div>
              
              <div className="p-4">
                {isLoading ? (
                  <div className="space-y-3">
                    <SkeletonCard />
                    <SkeletonCard />
                    <SkeletonCard />
                  </div>
                ) : availableJobs.length === 0 ? (
                  <div className="text-center py-8">
                    <Briefcase className="w-12 h-12 mx-auto mb-3 text-gray-300" />
                    <p className="text-sm text-gray-600">No new jobs available</p>
                    <p className="text-xs text-gray-500 mt-1">Check back soon for new opportunities</p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {availableJobs.slice(0, 3).map(job => {
                      const isNew = new Date().getTime() - new Date(job.created_at).getTime() < 3600000; // 1 hour
                      
                      return (
                        <div 
                          key={job.id}
                          onClick={() => navigate(`/jobs/${job.id}`)}
                          className="p-4 bg-white border border-gray-200 rounded-lg hover:shadow-md transition-shadow cursor-pointer"
                        >
                          <div className="flex justify-between items-start mb-2">
                            <h4 className="font-medium text-gray-900">{job.title}</h4>
                            {isNew && (
                              <Badge variant="success" size="sm">New - {getRelativeTime(job.created_at)}</Badge>
                            )}
                          </div>
                          <p className="text-sm text-gray-600 line-clamp-2 mb-2">{job.description}</p>
                          <div className="flex items-center justify-between text-xs text-gray-500">
                            <span>📍 {job.location}</span>
                            {(job.budget_min || job.budget_max) && (
                              <span>€{job.budget_min || 0}-€{job.budget_max || 0}</span>
                            )}
                          </div>
                        </div>
                      );
                    })}
                    {availableJobs.length > 3 && (
                      <Button variant="ghost" size="sm" fullWidth onClick={() => navigate('/provider-jobs')}>
                        View All {availableJobs.length} Available Jobs
                      </Button>
                    )}
                  </div>
                )}
              </div>
            </Card>
          </div>

          {/* Sidebar */}
          <div className="space-y-6">
            {/* Performance Stats - Moved to Sidebar */}
            <Card>
              <div className="p-4 border-b border-gray-200">
                <h3 className="font-semibold text-gray-900 flex items-center gap-2">
                  <Activity className="w-4 h-4 text-primary-600" />
                  Performance
                </h3>
              </div>
              <div className="p-4 space-y-3">
                {/* This Month Earnings */}
                <div className="p-3 bg-primary-50 rounded-lg border-l-4 border-l-primary-500">
                  <div className="flex items-center justify-between mb-1">
                    <div className="flex items-center gap-2">
                      <DollarSign className="w-4 h-4 text-primary-600" />
                      <span className="text-xs font-medium text-gray-600">This Month</span>
                    </div>
                    {earningsTrend !== 0 && (
                      <Badge variant={earningsTrend > 0 ? 'success' : 'error'} size="sm">
                        {earningsTrend > 0 ? <TrendingUp className="w-3 h-3 mr-1" /> : <TrendingDown className="w-3 h-3 mr-1" />}
                        {Math.abs(earningsTrend)}%
                      </Badge>
                    )}
                  </div>
                  <div className="text-xl font-bold text-gray-900">€{thisMonthEarnings.toLocaleString()}</div>
                  {lastMonthEarnings > 0 && (
                    <div className="text-xs text-gray-600 mt-1">
                      {earningsTrend > 0 ? '+' : ''}€{(thisMonthEarnings - lastMonthEarnings).toLocaleString()} vs last month
                    </div>
                  )}
                </div>

                {/* Completion Rate */}
                <div className="p-3 bg-emerald-50 rounded-lg border-l-4 border-l-emerald-500">
                  <div className="flex items-center justify-between gap-3">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-1">
                        <CheckCircle className="w-4 h-4 text-emerald-600" />
                        <span className="text-xs font-medium text-gray-600">Completion Rate</span>
                      </div>
                      <div className="text-xs text-gray-600 mt-1">
                        {completedJobs.length} completed, {activeJobs.length} active
                      </div>
                    </div>
                    <CircularProgress 
                      progress={completionRate} 
                      size={60}
                      strokeWidth={6}
                      variant="success"
                    />
                  </div>
                </div>

                {/* Quote Win Rate */}
                <div className="p-3 bg-warning-50 rounded-lg border-l-4 border-l-warning-500">
                  <div className="flex items-center gap-2 mb-1">
                    <FileText className="w-4 h-4 text-warning-600" />
                    <span className="text-xs font-medium text-gray-600">Quote Win Rate</span>
                  </div>
                  <div className="text-xl font-bold text-gray-900">{quoteWinRate}%</div>
                  <div className="text-xs text-gray-600 mt-1">
                    {activeJobs.length} accepted of {quotesSent.length} sent
                  </div>
                </div>

                {/* Available Jobs */}
                <div className="p-3 bg-blue-50 rounded-lg border-l-4 border-l-blue-500">
                  <div className="flex items-center gap-2 mb-1">
                    <Briefcase className="w-4 h-4 text-blue-600" />
                    <span className="text-xs font-medium text-gray-600">Available Jobs</span>
                  </div>
                  <div className="text-xl font-bold text-gray-900">{availableJobs.length}</div>
                  <div className="text-xs text-gray-600 mt-1">
                    {newJobsToday} posted today
                  </div>
                </div>
              </div>
            </Card>

            {/* Quote Templates Card - Moved to Sidebar */}
            <Card>
              <div className="p-4 border-b border-gray-200">
                <div className="flex items-center gap-2">
                  <FileText className="w-4 h-4 text-purple-600" />
                  <h3 className="font-semibold text-gray-900">Templates</h3>
                </div>
              </div>
              
              <div className="p-4">
                <div className="grid grid-cols-3 gap-2 mb-3">
                  <div className="text-center p-2 bg-purple-50 rounded-lg">
                    <div className="text-xl font-bold text-purple-600">{templateStats.totalTemplates}</div>
                    <div className="text-[10px] text-gray-600 mt-0.5">Templates</div>
                  </div>
                  <div className="text-center p-2 bg-blue-50 rounded-lg">
                    <div className="text-xl font-bold text-blue-600">{templateStats.totalUsage}</div>
                    <div className="text-[10px] text-gray-600 mt-0.5">Used</div>
                  </div>
                  <div className="text-center p-2 bg-emerald-50 rounded-lg">
                    <div className="text-xl font-bold text-emerald-600">{templateStats.averageSuccessRate}%</div>
                    <div className="text-[10px] text-gray-600 mt-0.5">Success</div>
                  </div>
                </div>
                
                <Button 
                  variant="outline" 
                  size="sm" 
                  fullWidth 
                  onClick={() => navigate('/quote-templates')}
                >
                  View Templates
                </Button>
              </div>
            </Card>

            {/* Completed Jobs Summary */}
            <Card>
              <div className="p-4 border-b border-gray-200">
                <h3 className="font-semibold text-gray-900 flex items-center gap-2">
                  <CheckCircle className="w-4 h-4 text-emerald-600" />
                  Recent Completions
                </h3>
              </div>
              <div className="p-4">
                {completedJobs.length === 0 ? (
                  <p className="text-sm text-gray-500 text-center py-4">No completed jobs yet</p>
                ) : (
                  <div className="space-y-3">
                    {completedJobs.slice(0, 3).map(job => (
                      <div key={job.id} className="text-sm">
                        <div className="font-medium text-gray-900">{job.title}</div>
                        <div className="text-xs text-gray-500 flex items-center justify-between mt-1">
                          <span>€{job.amount?.toLocaleString()}</span>
                          <span>{new Date(job.completed_date).toLocaleDateString()}</span>
                        </div>
                      </div>
                    ))}
                    {completedJobs.length > 3 && (
                      <Button variant="ghost" size="sm" fullWidth onClick={() => navigate('/provider/my-jobs?tab=completed')}>
                        View All ({completedJobs.length})
                      </Button>
                    )}
                  </div>
                )}
              </div>
            </Card>

            {/* Performance Tips */}
            <Card className="bg-gradient-to-br from-blue-50 to-blue-100 border-blue-200">
              <div className="p-4">
                <h3 className="font-semibold text-blue-900 mb-3 flex items-center gap-2">
                  💡 Performance Tips
                </h3>
                <div className="space-y-2 text-sm text-blue-800">
                  {quoteWinRate < 50 && !dismissedTips.includes('quote-win-rate') && (
                    <div className="bg-white/60 p-3 rounded-lg relative">
                      <button
                        onClick={() => dismissTip('quote-win-rate')}
                        className="absolute top-2 right-2 text-blue-400 hover:text-blue-600"
                      >
                        ×
                      </button>
                      <p className="font-medium mb-1">Improve Quote Acceptance</p>
                      <p className="text-xs pr-4">Add detailed breakdowns and photos to increase win rate by 30%</p>
                    </div>
                  )}
                  {urgentQuotes > 0 && !dismissedTips.includes('urgent-quotes') && (
                    <div className="bg-white/60 p-3 rounded-lg relative">
                      <button
                        onClick={() => dismissTip('urgent-quotes')}
                        className="absolute top-2 right-2 text-blue-400 hover:text-blue-600"
                      >
                        ×
                      </button>
                      <p className="font-medium mb-1">Respond Faster</p>
                      <p className="text-xs pr-4">You have {urgentQuotes} quote{urgentQuotes > 1 ? 's' : ''} pending over 24h. Quick responses win 40% more jobs</p>
                    </div>
                  )}
                  {availableJobs.length > 5 && !dismissedTips.includes('high-demand') && (
                    <div className="bg-white/60 p-3 rounded-lg relative">
                      <button
                        onClick={() => dismissTip('high-demand')}
                        className="absolute top-2 right-2 text-blue-400 hover:text-blue-600"
                      >
                        ×
                      </button>
                      <p className="font-medium mb-1">High Demand Period</p>
                      <p className="text-xs pr-4">{availableJobs.length} jobs available in your area - great time to quote!</p>
                    </div>
                  )}
                  {templateStats.totalTemplates === 0 && !dismissedTips.includes('create-templates') && (
                    <div className="bg-white/60 p-3 rounded-lg relative">
                      <button
                        onClick={() => dismissTip('create-templates')}
                        className="absolute top-2 right-2 text-blue-400 hover:text-blue-600"
                      >
                        ×
                      </button>
                      <p className="font-medium mb-1">Save Time with Templates</p>
                      <p className="text-xs pr-4">Create quote templates to respond 3x faster to common jobs</p>
                    </div>
                  )}
                  {completedJobs.length > 0 && completedJobs.length < 5 && !dismissedTips.includes('build-reputation') && (
                    <div className="bg-white/60 p-3 rounded-lg relative">
                      <button
                        onClick={() => dismissTip('build-reputation')}
                        className="absolute top-2 right-2 text-blue-400 hover:text-blue-600"
                      >
                        ×
                      </button>
                      <p className="font-medium mb-1">Build Your Reputation</p>
                      <p className="text-xs pr-4">Complete {5 - completedJobs.length} more job{5 - completedJobs.length > 1 ? 's' : ''} to unlock premium features</p>
                    </div>
                  )}
                  {quotesSent.length === 0 && availableJobs.length > 0 && !dismissedTips.includes('start-quoting') && (
                    <div className="bg-white/60 p-3 rounded-lg relative">
                      <button
                        onClick={() => dismissTip('start-quoting')}
                        className="absolute top-2 right-2 text-blue-400 hover:text-blue-600"
                      >
                        ×
                      </button>
                      <p className="font-medium mb-1">Start Quoting</p>
                      <p className="text-xs pr-4">{availableJobs.length} jobs waiting for quotes. Send your first quote today!</p>
                    </div>
                  )}
                  {/* Show default tip if all are dismissed */}
                  {dismissedTips.length >= 6 && (
                    <div className="bg-white/60 p-3 rounded-lg text-center">
                      <p className="text-xs">You're all caught up! Keep up the great work 🎉</p>
                    </div>
                  )}
                </div>
              </div>
            </Card>
          </div>
        </div>

      {/* Floating Action Button for Mobile & Desktop */}
      <div className="fixed bottom-20 md:bottom-6 right-4 md:right-6 z-30">
        <div className="relative group fab-container">
          <button
            className="w-14 h-14 bg-primary-600 text-white rounded-full shadow-lg flex items-center justify-center hover:bg-primary-700 transition-all hover:scale-110"
            onClick={() => setShowFabMenu(!showFabMenu)}
          >
            <span className="text-2xl font-bold">+</span>
          </button>
          
          {showFabMenu && (
            <div className="absolute bottom-16 right-0 bg-white rounded-lg shadow-xl border border-gray-200 py-2 w-48">
              <button
                onClick={() => {
                  navigate('/provider-jobs');
                  setShowFabMenu(false);
                }}
                className="w-full px-4 py-3 text-left hover:bg-gray-50 flex items-center gap-3 text-sm"
              >
                <Briefcase className="w-4 h-4 text-primary-600" />
                <span>Browse Jobs</span>
              </button>
              <button
                onClick={() => {
                  navigate('/invoices');
                  setShowFabMenu(false);
                }}
                className="w-full px-4 py-3 text-left hover:bg-gray-50 flex items-center gap-3 text-sm"
              >
                <FileText className="w-4 h-4 text-emerald-600" />
                <span>Invoices</span>
              </button>
              <button
                onClick={() => {
                  navigate('/calendar');
                  setShowFabMenu(false);
                }}
                className="w-full px-4 py-3 text-left hover:bg-gray-50 flex items-center gap-3 text-sm"
              >
                <Calendar className="w-4 h-4 text-blue-600" />
                <span>Calendar</span>
              </button>
              <button
                onClick={() => {
                  navigate('/analytics');
                  setShowFabMenu(false);
                }}
                className="w-full px-4 py-3 text-left hover:bg-gray-50 flex items-center gap-3 text-sm"
              >
                <BarChart3 className="w-4 h-4 text-purple-600" />
                <span>Analytics</span>
              </button>
              <button
                onClick={() => {
                  navigate('/quote-templates');
                  setShowFabMenu(false);
                }}
                className="w-full px-4 py-3 text-left hover:bg-gray-50 flex items-center gap-3 text-sm"
              >
                <FileText className="w-4 h-4 text-orange-600" />
                <span>Quote Templates</span>
              </button>
              <button
                onClick={() => {
                  navigate('/settings/provider');
                  setShowFabMenu(false);
                }}
                className="w-full px-4 py-3 text-left hover:bg-gray-50 flex items-center gap-3 text-sm border-t border-gray-100"
              >
                <Settings className="w-4 h-4 text-gray-600" />
                <span>Settings</span>
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Mobile Bottom Navigation */}
      <div className="fixed bottom-0 left-0 right-0 bg-white border-t border-gray-200 md:hidden z-40 safe-area-inset-bottom">
        <div className="grid grid-cols-4 gap-1 p-2">
          <button
            onClick={() => navigate('/dashboard')}
            className="flex flex-col items-center gap-1 p-2 rounded-lg bg-primary-50 text-primary-600"
          >
            <Home className="w-5 h-5" />
            <span className="text-xs font-medium">Home</span>
          </button>
          <button
            onClick={() => navigate('/provider-jobs')}
            className="flex flex-col items-center gap-1 p-2 rounded-lg hover:bg-gray-100 text-gray-600 relative"
          >
            <Briefcase className="w-5 h-5" />
            <span className="text-xs">Jobs</span>
            {availableJobs.length > 0 && (
              <span className="absolute top-1 right-1 w-5 h-5 bg-red-500 text-white text-xs rounded-full flex items-center justify-center font-bold">
                {availableJobs.length > 9 ? '9+' : availableJobs.length}
              </span>
            )}
          </button>
          <button
            onClick={() => navigate('/provider/quotes')}
            className="flex flex-col items-center gap-1 p-2 rounded-lg hover:bg-gray-100 text-gray-600 relative"
          >
            <FileText className="w-5 h-5" />
            <span className="text-xs">Quotes</span>
            {quotesSent.length > 0 && (
              <span className="absolute top-1 right-1 w-5 h-5 bg-yellow-500 text-white text-xs rounded-full flex items-center justify-center font-bold">
                {quotesSent.length > 9 ? '9+' : quotesSent.length}
              </span>
            )}
          </button>
          <button
            onClick={() => navigate('/profile')}
            className="flex flex-col items-center gap-1 p-2 rounded-lg hover:bg-gray-100 text-gray-600"
          >
            <User className="w-5 h-5" />
            <span className="text-xs">Profile</span>
          </button>
        </div>
      </div>

      {/* Complete Job Modal */}
      {showCompleteModal && selectedJob && (
        <CompleteJobModal
          jobTitle={selectedJob.title}
          quotedAmount={selectedJob.amount}
          onClose={() => {
            setShowCompleteModal(false);
            setSelectedJob(null);
          }}
          onComplete={handleCompleteJob}
        />
      )}
      </main>
    </div>
  );
}
